import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronRight } from 'lucide-react';
import { theme } from '../styles/theme';

const NavigationContainer = styled.div`
  position: sticky;
  top: 0;
  background: ${theme.colors.surface};
  border-bottom: 1px solid ${theme.colors.borderLight};
  z-index: 100;
  padding: ${theme.spacing.md} 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  /* 🚀 MOBİL SCROLL OPTİMİZASYONU */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: auto;
  
  /* iPhone için özel optimizasyonlar */
  @supports (-webkit-touch-callout: none) {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-perspective: 1000px;
    perspective: 1000px;
  }
`;

const NavigationWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing.sm};
  }
`;

const CategoryList = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: ${theme.spacing.xs} 0;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* iPhone için momentum scrolling optimizasyonu */
  @supports (-webkit-touch-callout: none) {
    -webkit-overflow-scrolling: touch;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    gap: 4px;
    padding: ${theme.spacing.xs};
    padding-right: 40px; /* Scroll indicator için yer */
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  right: ${theme.spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.colors.gradient.accent};
  color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(217, 119, 6, 0.3);
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
  
  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const CategoryButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: 20px;
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  border: 1px solid transparent;
  min-height: 44px;
  cursor: pointer;
  
  /* Mobil optimizasyon */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: auto;
  
  ${({ isActive }) => isActive ? `
    background: ${theme.colors.gradient.accent};
    color: ${theme.colors.text.white};
    border-color: ${theme.colors.primary};
    box-shadow: 0 4px 15px rgba(217, 119, 6, 0.3);
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  ` : `
    background: ${theme.colors.glass.background};
    backdrop-filter: ${theme.colors.glass.backdrop};
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.glass.border};
    
    /* Sadece masaüstünde hover efekti */
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${theme.colors.glass.backgroundHover};
        color: ${theme.colors.text.white};
        border-color: ${theme.colors.primary};
        transform: translate3d(0, -2px, 0);
        box-shadow: 0 4px 15px rgba(217, 119, 6, 0.2);
        will-change: transform, background-color;
      }
    }
    
    /* Mobil touch feedback */
    @media (max-width: 767px) {
      &:active {
        transform: translate3d(0, 1px, 0) scale(0.95);
        transition: transform 0.1s ease;
      }
    }
    
    /* iPhone için özel touch optimizasyonları */
    @supports (-webkit-touch-callout: none) {
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
      
      &:active {
        -webkit-transform: translate3d(0, 1px, 0) scale(0.98);
        transform: translate3d(0, 1px, 0) scale(0.98);
        transition: transform 0.08s ease;
      }
    }
  `}
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: ${theme.fontSizes.xs};
    gap: 2px;
    min-width: auto;
    flex-shrink: 0;
  }
`;

const CategoryIcon = styled.span`
  font-size: ${theme.fontSizes.lg};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes.md};
  }
`;

export const CategoryNavigation = ({ categories, activeCategory, onCategoryChange }) => {
  const categoryListRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const lastActionTimeRef = useRef(0);

  const isIOS = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const iOSDevice = /iPad|iPhone|iPod/.test(ua);
    const iPadOS13Up = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOSDevice || iPadOS13Up;
  };

  const getScrollElement = () => {
    if (typeof document === 'undefined') return null;
    // iOS ve farklı tarayıcılar için güvenli scrollingElement seçimi
    return document.scrollingElement || document.documentElement || document.body;
  };

  const animateScrollTo = (targetY, duration = 220) => {
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollEl = getScrollElement();
    if (!scrollEl) return;
    if (prefersReduced) {
      scrollEl.scrollTop = targetY;
      return;
    }

    const startY = scrollEl.scrollTop;
    const distance = targetY - startY;
    const startTime = performance.now();

    // iPhone için daha yumuşak easing fonksiyonu
    const easeInOutCubic = isIOS() 
      ? (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t  // iOS için daha yumuşak
      : (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // Diğer cihazlar için

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutCubic(progress);
      
      // iOS'ta daha hassas scroll pozisyonu
      if (isIOS()) {
        scrollEl.scrollTop = Math.round(startY + distance * eased);
      } else {
        scrollEl.scrollTop = startY + distance * eased;
      }
      
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  // Scroll indicator kontrolü - throttled
  useEffect(() => {
    let ticking = false;
    
    const checkScrollIndicator = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (categoryListRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = categoryListRef.current;
            const hasMoreContent = scrollLeft + clientWidth < scrollWidth - 10;
            setShowScrollIndicator(hasMoreContent);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    checkScrollIndicator();
    
    const listElement = categoryListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', checkScrollIndicator, { passive: true });
      window.addEventListener('resize', checkScrollIndicator, { passive: true });
      
      return () => {
        listElement.removeEventListener('scroll', checkScrollIndicator, { passive: true });
        window.removeEventListener('resize', checkScrollIndicator, { passive: true });
      };
    }
  }, [categories]);

  // Aktif kategori değişince navigator'ı kaydır
  useEffect(() => {
    if (categoryListRef.current && activeCategory) {
      const activeButton = categoryListRef.current.querySelector(`[data-category="${activeCategory}"]`);
      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeCategory]);

  const scrollToCategory = (categoryId) => {
    const element = document.getElementById(categoryId);
    if (!element) return;

    const offset = 120; // Navigation height + padding
    const scrollEl = getScrollElement();
    const elementPosition = element.getBoundingClientRect().top;
    const currentScrollTop = scrollEl ? scrollEl.scrollTop : window.pageYOffset;
    const offsetPosition = elementPosition + currentScrollTop - offset;

    // iOS'ta özel optimizasyon - momentum scrolling ve yumuşak geçiş
    if (isIOS()) {
      // iOS'ta momentum scrolling'i durdur
      if (scrollEl && scrollEl.style) {
        scrollEl.style.webkitOverflowScrolling = 'auto';
      }
      
      // Daha uzun ve yumuşak animasyon süresi iPhone için
      const duration = 350; // iPhone için daha uzun süre
      
      // İlk frame'de görsel tepki için küçük bir itme
      const current = scrollEl ? scrollEl.scrollTop : window.pageYOffset;
      const delta = offsetPosition - current;
      
      if (Math.abs(delta) > 1) {
        if (scrollEl) {
          scrollEl.scrollTop = current + (delta > 0 ? 2 : -2);
        } else {
          window.scrollTo(0, current + (delta > 0 ? 2 : -2));
        }
      }
      
      // Yumuşak animasyon başlat
      animateScrollTo(offsetPosition, duration);
      
      // Animasyon bittikten sonra momentum scrolling'i geri aç
      setTimeout(() => {
        if (scrollEl && scrollEl.style) {
          scrollEl.style.webkitOverflowScrolling = 'touch';
        }
      }, duration + 50);
      
    } else {
      // Desktop ve diğer cihazlar için native smooth scroll
      try {
        const scrollEl = getScrollElement();
        if (scrollEl && typeof scrollEl.scrollTo === 'function') {
          scrollEl.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      } catch (_) {
        animateScrollTo(offsetPosition, 220);
      }
    }
    
    // Tıklandığı anda üstte seçili gösterim için parent'a bildir
    onCategoryChange(categoryId);
  };

  const handleClick = (e, categoryId) => {
    // Touch sonrası gelen ghost click'i yut
    if (Date.now() - lastActionTimeRef.current < 800) {
      e.preventDefault();
      return;
    }
    scrollToCategory(categoryId);
  };

  const handleTouchStart = (e, categoryId) => {
    e.preventDefault();
    lastActionTimeRef.current = Date.now();
    
    // iPhone'da touch feedback için haptic feedback ekle
    if (isIOS() && navigator.vibrate) {
      navigator.vibrate(10); // Çok kısa titreşim
    }
    
    scrollToCategory(categoryId);
  };

  const handleMouseDown = (e, categoryId) => {
    // Masaüstünde anında tepki ver
    scrollToCategory(categoryId);
  };

  return (
    <NavigationContainer>
      <NavigationWrapper>
        <CategoryList ref={categoryListRef}>
          {categories.map((category) => (
            <CategoryButton
              key={category.id}
              data-category={category.id}
              isActive={activeCategory === category.id}
              onClick={(e) => handleClick(e, category.id)}
              onTouchStart={(e) => handleTouchStart(e, category.id)}
              onMouseDown={(e) => handleMouseDown(e, category.id)}
            >
              <CategoryIcon>{category.icon}</CategoryIcon>
              {category.name}
            </CategoryButton>
          ))}
        </CategoryList>
        
        <ScrollIndicator show={showScrollIndicator}>
          <ChevronRight size={16} />
        </ScrollIndicator>
      </NavigationWrapper>
    </NavigationContainer>
  );
};
